// API functions for reporting endpoints.

import type { ApiClient } from './client'

// --- Types: Risk Heatmap ---

export interface HeatmapLevel {
  value: number
  label: string
}

export interface HeatmapGridConfig {
  likelihood_levels: HeatmapLevel[]
  impact_levels: HeatmapLevel[]
}

export interface HeatmapPermittedCell {
  likelihood_level: number
  impact_level: number
}

export interface HeatmapRisk {
  id: string
  name: string
  register_id: string
  lifecycle_state: string
  residual_likelihood: number
  residual_max_impact: number
  outside_appetite: boolean
  stale_taxonomy: boolean
  taxonomy_node_name: string
  owner_display_name: string
}

export interface HeatmapGroup {
  appetite_id: string
  taxonomy_node_id: string
  taxonomy_node_name: string
  taxonomy_node_path: string
  taxonomy_breadcrumb: string
  statement: string | null
  permitted_cells: HeatmapPermittedCell[]
  risks: HeatmapRisk[]
  total_risk_count: number
}

export interface RiskHeatmapResponse {
  groups: HeatmapGroup[]
  grid: HeatmapGridConfig
}

export interface RiskHeatmapFilters {
  exclude_children?: boolean
  taxonomy_node_id?: string
}

// --- Types: Events Last Period ---

export interface EventTypeCount {
  event_type_id: string
  event_type_name: string
  count: number
}

export interface EventReportRow {
  id: string
  title: string
  event_type_name: string
  category_name: string
  event_date: string
  lifecycle_state: string
  recorded_by_name: string
  linked_risk_count: number
  linked_action_count: number
}

export interface EventsLastPeriodResponse {
  period_days: number
  period_start: string
  period_end: string
  total_count: number
  by_type: EventTypeCount[]
  events: EventReportRow[]
}

// --- Types: Control Assessments Last Period ---

export interface EffectivenessCount {
  rating: string
  count: number
}

export interface AssessmentReportRow {
  control_id: string
  control_name: string
  category_name: string
  assessment_type: string
  rating: string
  assessed_by_name: string
  assessed_at: string
  notes: string
}

export interface ControlAssessmentsResponse {
  period_days: number
  period_start: string
  period_end: string
  total_assessments: number
  by_effectiveness: EffectivenessCount[]
  assessments: AssessmentReportRow[]
}

// --- Types: Controls Summary ---

export interface LifecycleStateCount {
  lifecycle_state: string
  count: number
}

export interface CategorySummary {
  category_id: string
  category_name: string
  total: number
  active: number
  draft: number
  retired: number
  effective_count: number
  partially_effective_count: number
  ineffective_count: number
  not_assessed_count: number
  overdue_assessment_count: number
}

export interface ControlSummaryRow {
  id: string
  name: string
  category_name: string
  lifecycle_state: string
  latest_design_rating: string
  latest_operating_rating: string
  latest_assessment_date: string
  assessment_overdue: boolean
  linked_risk_count: number
  owner_display_name: string
}

export interface ControlsSummaryResponse {
  total_controls: number
  by_state: LifecycleStateCount[]
  by_category: CategorySummary[]
  controls: ControlSummaryRow[]
}

// --- API factory ---

export function reportingApi(api: ApiClient) {
  return {
    riskHeatmap: (filters?: RiskHeatmapFilters) => {
      const params = new URLSearchParams()
      if (filters?.exclude_children) params.set('exclude_children', 'true')
      if (filters?.taxonomy_node_id) params.set('taxonomy_node_id', filters.taxonomy_node_id)
      const qs = params.toString()
      return api.get<RiskHeatmapResponse>(`/reporting/risk-heatmap${qs ? `?${qs}` : ''}`)
    },

    eventsLastPeriod: (days?: number) => {
      const params = new URLSearchParams()
      if (days && days !== 30) params.set('days', String(days))
      const qs = params.toString()
      return api.get<EventsLastPeriodResponse>(
        `/reporting/events-last-period${qs ? `?${qs}` : ''}`,
      )
    },

    controlAssessmentsLastPeriod: (days?: number) => {
      const params = new URLSearchParams()
      if (days && days !== 30) params.set('days', String(days))
      const qs = params.toString()
      return api.get<ControlAssessmentsResponse>(
        `/reporting/control-assessments-last-period${qs ? `?${qs}` : ''}`,
      )
    },

    controlsSummary: () => api.get<ControlsSummaryResponse>('/reporting/controls-summary'),
  }
}
